/**
 * Layout principal de l'application
 * Sidebar + contenu principal
 */

import { useState, useEffect, useRef, type ReactNode } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
    Settings,
    LogOut,
    Menu,
    X,
    ChevronRight as _ChevronRight,
    ChevronDown,
    Lock,
    MailWarning,
    MessageSquare,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { authApi } from '@/services/api';
import { useToast } from '@/components/ui/toast';
import { useRealtimeEvents } from '@/hooks/useRealtimeEvents';
import { useSubscription } from '@/hooks/useSubscription';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { TrialBanner } from '@/components/TrialBanner';
import { SystemBanner } from '@/components/SystemBanner';
import { SubscriptionBlockedOverlay } from '@/components/SubscriptionBlockedOverlay';
import { PricingModal } from '@/components/PricingModal';
import { FeedbackButton } from '@/components/FeedbackButton';
import { InstallAppPrompt } from '@/components/InstallAppPrompt';
import { DemoBanner } from '@/components/DemoBanner';
import { CoachmarkProvider } from '@/components/coachmarks';
import { Button } from '@/components/ui';
import { estFormuleRetenue, EVENEMENT_HORS_FORMULE, routeOuverteFormuleRetenue } from '@/lib/formule';
import type { User } from '@/types';
import { navGroups, PAGE_TITLES, type NavGroup } from './navigation';

/** Les deux rubriques de la formule Retenue, en haut de sa barre, dans cet ordre. */
const RUBRIQUES_FORMULE_RETENUE = ['/retentions', '/clients'];

const toutesLesRubriques = navGroups.flatMap((group) => group.items);
const rubriquesFormuleRetenue = RUBRIQUES_FORMULE_RETENUE.map(
    (to) => toutesLesRubriques.find((item) => item.to === to)!
);
const groupesOffreComplete: NavGroup[] = navGroups.map((group) => ({
    ...group,
    items: group.items.filter((item) => !RUBRIQUES_FORMULE_RETENUE.includes(item.to)),
}));

/** Un refus hors formule arrive souvent en rafale (plusieurs requêtes) : un seul message. */
const DELAI_MESSAGE_HORS_FORMULE_MS = 4000;

interface GroupesNavProps {
    groupes: NavGroup[];
    user: User | null;
    /** Rubriques de l'offre complète vues depuis la formule Retenue : grisées, cadenas. */
    verrouillees?: boolean;
    onNavigate: () => void;
}

function GroupesNav({ groupes, user, verrouillees = false, onNavigate }: GroupesNavProps): ReactNode {
    return groupes.map((group, gi) => {
        const visibleItems = group.items.filter(
            (item) => !item.module || user?.[item.module] !== false
        );
        if (visibleItems.length === 0) return null;
        return (
            <div key={gi} className={gi > 0 ? 'mt-4' : ''}>
                {group.label && (
                    <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-[#64748B] dark:text-[#94A3B8]">
                        {group.label}
                    </p>
                )}
                <div className="space-y-0.5">
                    {visibleItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            onClick={onNavigate}
                            className={({ isActive }) =>
                                cn(
                                    'flex items-center gap-3 px-3 py-2 min-h-11 lg:min-h-0 rounded-lg transition-colors',
                                    'text-sm font-medium',
                                    isActive && !verrouillees
                                        ? 'bg-[#0F2744] dark:bg-[#2563EB] text-white'
                                        : 'text-[#64748B] dark:text-[#94A3B8] hover:bg-[#F1F5F9] dark:hover:bg-[#334155] hover:text-[#1E293B] dark:hover:text-[#F1F5F9]',
                                    verrouillees && 'opacity-60'
                                )
                            }
                        >
                            <item.icon className="h-4 w-4 shrink-0" />
                            <span>{item.label}</span>
                            {verrouillees && <Lock className="ml-auto h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
                        </NavLink>
                    ))}
                </div>
            </div>
        );
    });
}

// ========================================
// Layout Component
// ========================================

interface LayoutProps {
    children: ReactNode;
}

export function Layout({ children }: LayoutProps): ReactNode {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const pageTitle = PAGE_TITLES[location.pathname] ?? '';
    const formuleRetenue = estFormuleRetenue(user);
    // Repliée par défaut ; ouverte d'emblée sur une rubrique fermée, pour montrer où l'on est.
    const [offreCompleteOuverte, setOffreCompleteOuverte] = useState(() => !routeOuverteFormuleRetenue(location.pathname));

    // Real-time cache invalidation via SSE
    useRealtimeEvents();

    // Scroll to top on page navigation
    useScrollToTop();

    const subscription = useSubscription(user);
    const [bannerDismissed, setBannerDismissed] = useState(false);
    const [blockedCode, setBlockedCode] = useState<string | undefined>(undefined);
    const [showBlocked, setShowBlocked] = useState(false);
    const [showEmailBanner, setShowEmailBanner] = useState(false);
    const [showPricing, setShowPricing] = useState(false);

    // Écouter les événements 402 du response interceptor
    useEffect(() => {
        const handler = (e: Event) => {
            const code = (e as CustomEvent).detail?.code;
            setBlockedCode(code);
            setShowBlocked(true);
        };
        window.addEventListener('subscription:blocked', handler);
        return () => window.removeEventListener('subscription:blocked', handler);
    }, []);

    // Écouter l'événement email non vérifié (403 EMAIL_NOT_VERIFIED)
    useEffect(() => {
        const handler = () => setShowEmailBanner(true);
        window.addEventListener('auth:email-not-verified', handler);
        return () => window.removeEventListener('auth:email-not-verified', handler);
    }, []);

    // Overlay affiché soit sur événement 402 (showBlocked), soit si déjà bloqué au chargement
    const blocked = showBlocked || subscription.urgency === 'expired';

    // Lock body scroll when mobile drawer is open (prevents background scroll/chaining)
    useEffect(() => {
        if (!sidebarOpen) return;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previous; };
    }, [sidebarOpen]);

    const toast = useToast();

    // Refus 403 « hors formule » malgré la garde des routes : pas une erreur, une rubrique fermée.
    const dernierMessageHorsFormule = useRef(0);
    useEffect(() => {
        const handler = () => {
            const maintenant = Date.now();
            if (maintenant - dernierMessageHorsFormule.current < DELAI_MESSAGE_HORS_FORMULE_MS) return;
            dernierMessageHorsFormule.current = maintenant;
            toast.info("Cette rubrique fait partie de l'offre complète.");
        };
        window.addEventListener(EVENEMENT_HORS_FORMULE, handler);
        return () => window.removeEventListener(EVENEMENT_HORS_FORMULE, handler);
    }, [toast]);

    const [resending, setResending] = useState(false);
    const resendVerification = async () => {
        setResending(true);
        try {
            await authApi.resendVerification();
            toast.success('Un nouveau lien vient de vous être envoyé.');
        } catch {
            toast.error("L'email n'est pas parti. Réessayez dans un instant.");
        } finally {
            setResending(false);
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    return (
        <>
            {blocked && <SubscriptionBlockedOverlay code={blockedCode} />}
            {showEmailBanner && (
                <div className="fixed top-0 left-0 right-0 z-60 bg-[#F59E0B] text-white px-4 py-2 pt-[max(0.5rem,env(safe-area-inset-top))] flex items-center justify-between gap-4 text-sm">
                    <div className="flex items-center gap-2">
                        <MailWarning className="h-4 w-4 shrink-0" />
                        <span>Confirmez votre adresse email pour accéder à toutes les fonctionnalités.</span>
                        {/* Sans ce bouton, un email de vérification perdu bloquait le
                            compte sans aucun moyen d'en recevoir un autre. */}
                        <button
                            onClick={resendVerification}
                            disabled={resending}
                            className="shrink-0 underline underline-offset-2 hover:opacity-75 disabled:opacity-60"
                        >
                            {resending ? 'Envoi…' : 'Renvoyer le lien'}
                        </button>
                    </div>
                    <button onClick={() => setShowEmailBanner(false)} className="shrink-0 hover:opacity-75 flex items-center justify-center h-11 w-11 -my-2">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}
            <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0F172A]">
                {/* Mobile Header */}
                <header className="lg:hidden fixed top-0 left-0 right-0 h-[calc(3.5rem+env(safe-area-inset-top))] pt-[env(safe-area-inset-top)] bg-white dark:bg-[#1E293B] border-b border-[#E2E8F0] dark:border-[#334155] z-40 pl-[max(0.75rem,env(safe-area-inset-left))] pr-[max(0.75rem,env(safe-area-inset-right))] flex items-center gap-2">
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className="h-11 w-11 -ml-2 flex items-center justify-center rounded-lg hover:bg-[#F1F5F9] dark:hover:bg-[#334155] transition-colors shrink-0"
                        aria-label="Ouvrir le menu"
                    >
                        <Menu className="h-5 w-5 dark:text-[#F1F5F9]" />
                    </button>
                    <h1 className="flex-1 min-w-0 text-base font-semibold text-[#1E293B] dark:text-[#F1F5F9] truncate">
                        {pageTitle}
                    </h1>
                </header>

                {/* Mobile Overlay */}
                {sidebarOpen && (
                    <div
                        className="lg:hidden fixed inset-0 bg-black/50 z-40"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                {/* Sidebar */}
                <aside
                    className={cn(
                        'fixed top-0 left-0 h-full bg-white dark:bg-[#1E293B] border-r border-[#E2E8F0] dark:border-[#334155] z-50 transition-transform duration-300',
                        'w-70 flex flex-col pl-[env(safe-area-inset-left)]',
                        'lg:translate-x-0',
                        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                    )}
                >
                    {/* Logo */}
                    <div className="h-16 px-6 pt-[env(safe-area-inset-top)] flex items-center justify-between border-b border-[#E2E8F0] dark:border-[#334155]">
                        <div className="flex items-center gap-3">
                            <img src="/logo.png" alt="BTPal" className="w-8 h-8 rounded-lg" />
                            <span className="text-xl font-bold text-[#1E293B] dark:text-[#F1F5F9] [font-family:var(--font-display)]">BTPal</span>
                        </div>
                        <button
                            onClick={() => setSidebarOpen(false)}
                            className="lg:hidden h-11 w-11 -mr-2 flex items-center justify-center rounded hover:bg-[#F1F5F9] dark:hover:bg-[#334155]"
                            aria-label="Fermer le menu"
                        >
                            <X className="h-5 w-5 dark:text-[#F1F5F9]" />
                        </button>
                    </div>

                    {/* User Info */}
                    <div className="px-4 py-4 border-b border-[#E2E8F0] dark:border-[#334155]">
                        <div className="flex items-center gap-3 p-2 rounded-lg bg-[#F8FAFC] dark:bg-[#0F172A]">
                            {user?.logoUrl ? (
                                <img src={user.logoUrl} alt={user.company || ''} className="w-10 h-10 rounded-lg object-contain" />
                            ) : (
                                <div className="w-10 h-10 rounded-full bg-[#0F2744] dark:bg-[#2563EB] flex items-center justify-center text-white font-bold">
                                    {user?.company?.charAt(0) || 'N'}
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[#1E293B] dark:text-[#F1F5F9] truncate">
                                    {user?.company || 'Mon entreprise'}
                                </p>
                                <p className="text-xs text-[#64748B] dark:text-[#94A3B8] truncate">
                                    {user?.email || ''}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 px-3 py-3 overflow-y-auto overscroll-contain">
                        {formuleRetenue ? (
                            <>
                                <div>
                                    <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-[#64748B] dark:text-[#94A3B8]">
                                        Formule Retenue
                                    </p>
                                    <GroupesNav
                                        groupes={[{ items: rubriquesFormuleRetenue }]}
                                        user={user}
                                        onNavigate={() => setSidebarOpen(false)}
                                    />
                                </div>
                                <div className="mt-6 pt-3 border-t border-[#E2E8F0] dark:border-[#334155]">
                                    <button
                                        type="button"
                                        aria-expanded={offreCompleteOuverte}
                                        aria-controls="nav-offre-complete"
                                        onClick={() => setOffreCompleteOuverte((ouverte) => !ouverte)}
                                        className="flex items-center gap-3 px-3 py-2 min-h-11 lg:min-h-0 rounded-lg w-full transition-colors text-sm font-medium text-[#64748B] dark:text-[#94A3B8] hover:bg-[#F1F5F9] dark:hover:bg-[#334155] hover:text-[#1E293B] dark:hover:text-[#F1F5F9]"
                                    >
                                        <span>Offre complète</span>
                                        <ChevronDown
                                            className={cn('ml-auto h-4 w-4 shrink-0 transition-transform', offreCompleteOuverte && 'rotate-180')}
                                            aria-hidden="true"
                                        />
                                    </button>
                                    {offreCompleteOuverte && (
                                        <div id="nav-offre-complete" className="mt-2">
                                            <GroupesNav
                                                groupes={groupesOffreComplete}
                                                user={user}
                                                verrouillees
                                                onNavigate={() => setSidebarOpen(false)}
                                            />
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <GroupesNav groupes={navGroups} user={user} onNavigate={() => setSidebarOpen(false)} />
                        )}
                    </nav>

                    {/* Bottom — Paramètres + Déconnexion */}
                    {/* La marge basse est celle du téléphone, pas une marge en plus : ajouter
                        12px à l'encoche laissait une bande vide sous les liens légaux, prise
                        sur la zone de défilement de la navigation. */}
                    <div className="px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] border-t border-[#E2E8F0] dark:border-[#334155] space-y-0.5">
                        <button
                            type="button"
                            onClick={() => {
                                setSidebarOpen(false);
                                window.dispatchEvent(new Event('feedback:open'));
                            }}
                            className="lg:hidden flex items-center gap-3 px-3 py-2 min-h-11 rounded-lg transition-colors text-sm font-medium w-full text-[#64748B] dark:text-[#94A3B8] hover:bg-[#F1F5F9] dark:hover:bg-[#334155] hover:text-[#1E293B] dark:hover:text-[#F1F5F9]"
                        >
                            <MessageSquare className="h-4 w-4 shrink-0" />
                            <span>Signaler / Suggérer</span>
                        </button>
                        <NavLink
                            to="/settings"
                            onClick={() => setSidebarOpen(false)}
                            className={({ isActive }) =>
                                cn(
                                    'flex items-center gap-3 px-3 py-2 min-h-11 lg:min-h-0 rounded-lg transition-colors text-sm font-medium w-full',
                                    isActive
                                        ? 'bg-[#0F2744] dark:bg-[#2563EB] text-white'
                                        : 'text-[#64748B] dark:text-[#94A3B8] hover:bg-[#F1F5F9] dark:hover:bg-[#334155] hover:text-[#1E293B] dark:hover:text-[#F1F5F9]'
                                )
                            }
                        >
                            <Settings className="h-4 w-4 shrink-0" />
                            <span>Paramètres</span>
                        </NavLink>
                        <Button
                            variant="ghost"
                            className="w-full justify-start gap-3 text-[#64748B] hover:text-[#EF4444] hover:bg-[#FEF2F2] dark:hover:bg-[#7F1D1D] py-2 min-h-11 lg:min-h-0 text-sm"
                            onClick={handleLogout}
                        >
                            <LogOut className="h-4 w-4 shrink-0" />
                            <span>Déconnexion</span>
                        </Button>

                        {/* Liens légaux */}
                        <div className="pt-2 border-t border-[#E2E8F0] dark:border-[#334155] flex flex-wrap gap-x-3 gap-y-1">
                            <a href="/mentions-legales" className="text-[10px] text-[#94A3B8] hover:text-[#64748B] dark:hover:text-[#F1F5F9]">Mentions légales</a>
                            <a href="/cgu" className="text-[10px] text-[#94A3B8] hover:text-[#64748B] dark:hover:text-[#F1F5F9]">CGU</a>
                            <a href="/politique-confidentialite" className="text-[10px] text-[#94A3B8] hover:text-[#64748B] dark:hover:text-[#F1F5F9]">Confidentialité</a>
                        </div>
                    </div>
                </aside>

                {/* Main Content */}
                <main className="lg:ml-70 min-h-screen pt-[calc(3.5rem+env(safe-area-inset-top))] lg:pt-0 pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]">
                    {/* Le mode exemple montre des devis et des factures : rien de la formule Retenue. */}
                    {!formuleRetenue && <DemoBanner />}
                    <SystemBanner />
                    {!bannerDismissed && (
                        <TrialBanner
                            subscription={subscription}
                            formuleRetenue={formuleRetenue}
                            onDismiss={() => setBannerDismissed(true)}
                            onUpgrade={() => setShowPricing(true)}
                        />
                    )}
                    {showPricing && <PricingModal onClose={() => setShowPricing(false)} />}
                    <div className="p-4 lg:p-6">
                        <CoachmarkProvider>{children}</CoachmarkProvider>
                    </div>
                </main>
            </div>
            <FeedbackButton />
            <InstallAppPrompt />
        </>
    );
}

export default Layout;
